import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DatePickerButton } from '@/components/date-picker-button'
import type { AppLocale } from '@/lib/i18n'

import {
  datePlaceholder,
  formatDisplayDate,
  parseDisplayDate,
  parseTimeInput,
  splitLocalDateTime,
} from '../date-input'

interface DateTimeFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  error?: string
  disabled?: boolean
  kind?: 'start' | 'end'
  min?: string
  max?: string
}

/**
 * Locale-enforced date + time control (D-050). Native `datetime-local` inputs
 * render the date order of the host browser, so the visible date is a text
 * field whose order follows the app locale while the form keeps the canonical
 * `YYYY-MM-DDTHH:mm` wall-clock value internally.
 */
export function DateTimeField({
  label,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  kind = 'start',
  min,
  max,
}: DateTimeFieldProps) {
  const { i18n, t } = useTranslation()
  const locale: AppLocale = i18n.language === 'en' ? 'en' : 'it'
  const id = useId()
  const errorId = `${id}-error`
  const hintId = `${id}-hint`
  const parts = splitLocalDateTime(value)
  const [dateDraft, setDateDraft] = useState<string | null>(null)
  const [timeDraft, setTimeDraft] = useState<{
    hours: string
    minutes: string
  } | null>(null)
  const [localError, setLocalError] = useState(false)
  const [timeError, setTimeError] = useState(false)

  const dateText = dateDraft ?? formatDisplayDate(parts.date, locale)
  const timeParts = timeDraft ?? {
    hours: parts.time.slice(0, 2) || '00',
    minutes: parts.time.slice(3, 5) || '00',
  }
  const timeText = `${timeParts.hours}:${timeParts.minutes}`
  const timeLabel = t(
    kind === 'start'
      ? 'activityForm.fields.startTime'
      : 'activityForm.fields.endTime',
  )

  const shownError = localError
    ? t('activityForm.validation.dateFormat', {
        format: datePlaceholder(locale),
      })
    : timeError
      ? t('activityForm.validation.timeFormat', { format: 'HH:mm' })
      : error

  function onDateChange(raw: string) {
    const next = raw.slice(0, 10)
    setDateDraft(next)
    if (!next.trim()) {
      setLocalError(false)
      onChange('')
      return
    }
    const parsed = parseDisplayDate(next, locale)
    if (!parsed) {
      setLocalError(true)
      // Clear the form value so an invalid partial date can never be
      // submitted from a stale value.
      onChange('')
      return
    }
    setLocalError(false)
    const parsedTime = parseTimeInput(timeText)
    if (timeParts.hours.length === 2 && timeParts.minutes.length === 2 && parsedTime) {
      setDateDraft(null)
      onChange(`${parsed}T${parsedTime}`)
    } else {
      // Keep the date visible while the user completes the time.
      onChange('')
    }
  }

  function onTimeChange(part: 'hours' | 'minutes', raw: string) {
    const next = { ...timeParts, [part]: raw.replace(/\D/g, '').slice(0, 2) }
    setTimeDraft(next)
    const parsedTime = parseTimeInput(`${next.hours}:${next.minutes}`)
    const parsedDate = parseDisplayDate(dateText, locale)
    if (next.hours.length !== 2 || next.minutes.length !== 2 || !parsedTime) {
      setTimeError(true)
      if (parsedDate) {
        setDateDraft(dateText)
        onChange('')
      }
      return
    }
    setTimeError(false)
    if (parsedDate) {
      setTimeDraft(null)
      setDateDraft(null)
      onChange(`${parsedDate}T${parsedTime}`)
    }
  }

  return (
    <div>
      <span className="field-label">{label}</span>
      <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] gap-2">
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={10}
            required
            disabled={disabled}
            value={dateText}
            onChange={(event) => onDateChange(event.target.value)}
            onBlur={onBlur}
            placeholder={datePlaceholder(locale)}
            aria-label={t(
              kind === 'start'
                ? 'activityForm.fields.startDate'
                : 'activityForm.fields.endDate',
            )}
            aria-invalid={Boolean(shownError) || undefined}
            aria-describedby={shownError ? errorId : undefined}
            className={`field-input pr-12 ${shownError ? 'border-danger' : ''}`}
          />
          <DatePickerButton
            label={t(
              kind === 'start'
                ? 'activityForm.fields.startDate'
                : 'activityForm.fields.endDate',
            )}
            value={parts.date || parseDisplayDate(dateText, locale) || ''}
            onChange={onDateChange}
            min={min}
            max={max}
            disabled={disabled}
          />
        </div>
        <div
          role="group"
          aria-label={timeLabel}
          className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1"
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={2}
            required
            disabled={disabled}
            value={timeParts.hours}
            onChange={(event) => onTimeChange('hours', event.target.value)}
            onBlur={onBlur}
            placeholder="HH"
            aria-label={`${timeLabel}: ${t('activityForm.fields.hours')}`}
            aria-invalid={Boolean(timeError) || undefined}
            aria-describedby={timeError ? errorId : undefined}
            className={`field-input min-w-0 px-1 text-center ${timeError ? 'border-danger' : ''}`}
          />
          <span aria-hidden="true">:</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={2}
            required
            disabled={disabled}
            value={timeParts.minutes}
            onChange={(event) => onTimeChange('minutes', event.target.value)}
            onBlur={onBlur}
            placeholder="mm"
            aria-label={`${timeLabel}: ${t('activityForm.fields.minutes')}`}
            aria-invalid={Boolean(timeError) || undefined}
            aria-describedby={timeError ? errorId : undefined}
            className={`field-input min-w-0 px-1 text-center ${timeError ? 'border-danger' : ''}`}
          />
        </div>
      </div>
      <p
        id={hintId}
        className="mt-1.5 text-xs leading-snug text-muted"
        hidden={!shownError}
      >
        {t('activityForm.validation.dateHint', {
          format: datePlaceholder(locale),
        })}
      </p>
      {shownError ? (
        <p id={errorId} className="mt-1.5 text-sm font-semibold text-danger">
          {shownError}
        </p>
      ) : null}
    </div>
  )
}
